import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import blogPosts from "../data/blogPostsData";
import BlogCards from "./BlogCards";
import { getRandomRelatedBlogPosts } from "../utils/blogRecommendations";
import "../styles/Blog.css";

const renderBlock = (block) => {
  if (typeof block === "string") {
    return <p key={block}>{block}</p>;
  }

  if (block.type === "h2") {
    return <h2 key={block.text}>{block.text}</h2>;
  }

  if (block.type === "list") {
    return (
      <ul className="blog-list" key={block.items.join("-")}>
        {block.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }

  if (block.type === "cta") {
    return (
      <aside className="blog-cta" key={block.title}>
        <h2>{block.title}</h2>
        <p>{block.text}</p>
        <Link to={block.linkHref}>{block.linkText}</Link>
      </aside>
    );
  }

  return <p key={block.text}>{block.text}</p>;
};

function BlogPostPage() {
  const { blogSlug } = useParams();
  const post = blogPosts.find((item) => item.slug === blogSlug);
  const relatedPosts = useMemo(
    () => (post ? getRandomRelatedBlogPosts(post.slug, 3) : []),
    [post]
  );

  if (!post) {
    return (
      <main className="blog-page">
        <section className="container blog-header">
          <h1>Articulo no encontrado</h1>
          <Link to="/blog">Volver al blog</Link>
        </section>
      </main>
    );
  }

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.date,
    dateModified: post.date,
    image: post.image
      ? `https://fundacionnican.com${post.image}`
      : "https://fundacionnican.com/cursos-enfermeria-quiropractica-nican.png",
    author: {
      "@type": "Organization",
      name: "Fundacion Nican",
    },
    publisher: {
      "@type": "Organization",
      name: "Fundacion Nican",
    },
    mainEntityOfPage: `https://fundacionnican.com/blog/${post.slug}`,
  };
  const articleImageUrl = post.image
    ? `https://fundacionnican.com${post.image}`
    : "https://fundacionnican.com/cursos-enfermeria-quiropractica-nican.png";

  return (
    <>
      <Helmet>
        <html lang="es-MX" />
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <meta property="og:image" content={articleImageUrl} />
        <meta name="twitter:image" content={articleImageUrl} />
        <link
          rel="canonical"
          href={`https://fundacionnican.com/blog/${post.slug}`}
        />
        <script type="application/ld+json">
          {JSON.stringify(articleJsonLd)}
        </script>
      </Helmet>

      <main className="blog-page">
        <article className="container blog-article">
          <Link className="blog-back-link" to="/blog">
            Volver al blog
          </Link>
          {post.date && <span className="blog-article-date">{post.date}</span>}
          <h1>{post.title}</h1>
          {post.image && (
            <img
              className="blog-hero-image"
              src={post.image}
              alt={post.imageAlt}
            />
          )}
          {post.content.map(renderBlock)}
          {relatedPosts.length > 0 && (
            <section className="blog-related-section">
              <div className="blog-related-heading">
                <span>Tambien te puede interesar</span>
                <h2>Sigue leyendo</h2>
              </div>
              <BlogCards
                posts={relatedPosts}
                className="blog-card-grid-three"
              />
            </section>
          )}
        </article>
      </main>
    </>
  );
}

export default BlogPostPage;
